'use client';
import { settingsHashToAccountPath } from '#pplx/frontend/apps/ask/settings/settingsHash.js';
import { useHash } from '#pplx/frontend/apps/ask/settings/useSettingsHash.js';
import { useIsUserLoggedIn } from '#pplx/frontend/auth/userHooks.js';
import { redirectWindow } from '#pplx/frontend/platform-components/redirectWindow.js';
import { QueryClientHelper } from '#pplx/frontend/react-query/QueryClientHelper.js';
import { useQuery } from '#pplx/frontend/react-query/useQuery.js';
import { usePathname } from '#pplx/frontend/router/usePathname.js';
import { authClient } from '#pplx_generated/typespec/rest/auth/client.js';
// Pages where targeted users can navigate without verifying. Narrow escape
// hatch: lets users on mismatched partner promos self-cancel via
// /account/details (the subscription page), including its canonical
// /#settings/account modal URL, or complete a legitimate purchase via
// /pro, /max, /billing, /checkout, /subscribe. Other
// /account/* routes (API portal, connectors, files, org, credits) are
// intentionally NOT exempted — verification still gates them. Also
// prevents the redirect loop on /phone-verification itself.
const ACCOUNT_DETAILS_PATH = '/account/details';
const VERIFICATION_EXEMPT_PREFIXES = [
    ACCOUNT_DETAILS_PATH,
    '/pro',
    '/max',
    '/billing',
    '/checkout',
    '/subscribe',
];
function isVerificationExemptPath(pathname) {
    if (pathname === '/phone-verification')
        return true;
    return VERIFICATION_EXEMPT_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(prefix + '/'));
}
export function usePhoneSmsVerification() {
    const isLoggedIn = useIsUserLoggedIn();
    const pathname = usePathname();
    const hash = useHash();
    const isAccountDetailsModal = pathname === '/' && settingsHashToAccountPath(hash) === ACCOUNT_DETAILS_PATH;
    const isExemptPage = isVerificationExemptPath(pathname) || isAccountDetailsModal;
    const { data, isLoading } = useQuery({
        queryKey: QueryClientHelper.makeEphemeralQueryKey('/api/auth/phone-verification/status'),
        queryFn: async () => {
            const { data, error } = await authClient.GET('/api/auth/phone-verification/status', 'phone-verification-status');
            if (error)
                throw error;
            return data;
        },
        enabled: isLoggedIn && !isExemptPage,
    });
    if (isLoggedIn && !isLoading && !isExemptPage && data?.requires_verification) {
        redirectWindow('/phone-verification', 'phone verification required');
        return { isLoading: true }; // prevent flash while redirecting
    }
    return { isLoading: isLoggedIn && isLoading && !isExemptPage };
}
//# sourceMappingURL=usePhoneSmsVerification.js.map